import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {SettingsService} from 'app/original-services/settings.service';
import {StorageService} from 'app/original-services/storage.service';
import {LoggerService} from 'app/original-services/logger.service';
import get from 'lodash-es/get';
import {BehaviorSubject, Observable, Subscription, tap} from 'rxjs';
import {finalize, map, shareReplay} from 'rxjs/operators';
import {AuthenticationService} from 'app/original-services/authentication.service';
import { IntercomService } from 'app/services/intercom/intercom.service';
import {IntegrationsModel} from '../original-models/integrations.model';
import {LevelType, SelectedLevel} from 'app/original-models/level-type.model';
import {todayOnlyRoles} from 'app/original-models/access.model';
import {ActivityTrackerService} from './activity-tracker.service';
import {ActivitiesAction, FeedGroup, ResourceArea} from 'app/common/constants';
import {TeamMembersCacheService} from './team-members-cache.service';
import {Company} from 'app/original-models/company.model';
import {IntercomSettings} from 'app/original-models/intercom-settings.model';
import {SlackAuth} from 'app/original-models/slack.model';

@Injectable({ providedIn: 'root' })
export class CompanyService {
    apiUrl: string;
    isLoggedIn = true;
    companiesList: any = [];
    private _currentCompanySubject = new BehaviorSubject<Company>(null);
    private _currentCompanyChanged = new BehaviorSubject<Company>(null);
    public _companiesSubject = new BehaviorSubject<Company[]>(null);
    private _companiesSubscription: Subscription;
    /** In-flight GET /companies/{id}/ per id, so concurrent callers share one request. */
    private _companyRequests = new Map<number, Observable<Company>>();
    private _initialized = false;

    /**
     * True from the start of a company switch until the sanctioned re-navigation settles.
     * The route guard uses it to abort the stale page's competing (reload:false) self-navigation
     * so the header's reload:true re-entry wins and the target page re-instantiates.
     * See FeatureFlagRouteGuardService and header.onCompanyChange.
     */
    public switchInProgress = false;
    private _switchTimer: any;

    currentCompanyChanged$ = this._currentCompanyChanged.asObservable();

    constructor(
        private http: HttpClient,
        private settings: SettingsService,
        private storage: StorageService,
        private authService: AuthenticationService,
        private intercom: IntercomService,
        private logger: LoggerService,
        private teamMembers: TeamMembersCacheService,
        private activityTrackerService: ActivityTrackerService
    ) {
        this.apiUrl = this.settings.apiUrl;

        this.authService.afterLogin$.subscribe(() => {
            this.isLoggedIn = true;
            this._companiesSubject.next(null);
            this._currentCompanySubject.next(null);
            this._currentCompanyChanged.next(null);
            this.updateCurrentCompany(null);
            this.updateCompanies(null);
            //this.fetchCompanies(true);
        });

        this.authService.beforeLogout$.subscribe(() => {
            this.isLoggedIn = false;
            this._companiesSubject.next(null);
            this._currentCompanySubject.next(null);
            this._currentCompanyChanged.next(null);
            this.updateCurrentCompany(null);
            this.updateCompanies(null);
        });
        this._companiesSubject.subscribe((data) => {
            this.companiesList = data || [];
            //  this.setCurrentCompany(data);
        });
    }

    setCurrentCompany(data) {
        if (data && data.length) {
            let currentCompanyId = this.storage.companyId;
            if (currentCompanyId) {
                //Update current company using companyId from storage
                let company = data.find((item) => item.id === currentCompanyId);
                if (company) {
                    this.updateCurrentCompany(company);
                } else {
                    let ownerCompanies = data.filter((item) => {
                        return (
                            item.isActive &&
                            item.isOwner(
                                this.storage.userId,
                                this.storage.model
                            )
                        );
                    });
                    let companies =
                        ownerCompanies.length > 0
                            ? ownerCompanies.sort((a, b) => a.order - b.order)
                            : data.filter((item) => item.isActive);
                    this.updateCurrentCompany(companies[0]);
                }
            } else {
                let ownerCompanies = data.filter((item) => {
                    return (
                        item.isActive &&
                        item.isOwner(this.storage.userId, this.storage.model)
                    )
                });
                let companies =
                    ownerCompanies.length > 0
                        ? ownerCompanies.sort((a, b) => a.order - b.order)
                        : data.filter((item) => item.isActive);
                this.updateCurrentCompany(companies[0]);
            }
        } else {
            if (
                this.storage.selectedLevel == null &&
                (!data || data.length === 0)
            ) {
                this.storage.selectedLevel = new SelectedLevel(
                    0,
                    LevelType.Company
                );
            }
        }
    }

    private updateCurrentCompany(company: Company) {
        if (company) {
            let changed =
                !this._currentCompanySubject?.value ||
                this._currentCompanySubject.value.id !== company.id;

            this.fetchCompany(company.id).subscribe((value) => {
                this.storage.companyId = company.id;
                this.storage.selectedLevel = {
                    id: company.id,
                    type: LevelType.Company,
                };
                if (changed) {
                    let log = {
                        info: {action: ActivitiesAction.login
                        },
                        target: {targetFeedKey: FeedGroup.company_various_activities, targetFeedId: `${company.id}`,
                         foreignParams: [{key: 'resource_area', value: ResourceArea.company}], parentFeeds: [] }
                    };

                    this.teamMembers.getCurrentMember(true).subscribe(() => {
                        this.activityTrackerService.log(log.info, log.target);
                        // On login getCurrentCompany() shares this same request and has already pushed
                        // `value` into both subjects; re-emitting it made Insights load its data twice.
                        if (this._currentCompanyChanged.value !== value) {
                            this._currentCompanyChanged.next(value);
                        }
                        setTimeout(() => {
                            if (this._currentCompanySubject.value !== value) {
                                this._currentCompanySubject.next(value);
                            }
                        }, 20);
                    });
                } else {
                    this._currentCompanySubject.next(value);
                }
            });
        } else {
            this.storage.companyId = null;
        }
    }

    updateCompanies(companies: Company[]) {
        this._companiesSubject.next(companies);
        this._companiesSubscription = null;
    }

    private updateCompaniesWithUpdatedItem(company: Company) {
        let companies = this._companiesSubject.value;
        let existingItemIndex = companies
            ? companies.findIndex((i) => i.id === company.id)
            : null;
        if (!companies) {
            companies = [];
        }
        if (existingItemIndex >= 0) {
            companies[existingItemIndex] = company;
        } else {
            companies.push(company);
        }
        this.updateCompanies(companies);
    }

    getCompanies(): Observable<Company[]> {
        this.fetchCompanies();
        return this._companiesSubject.asObservable();
    }

    private fetchCompanies(refresh?: boolean): void {
        if (
            refresh ||
            ((!this._companiesSubject.value ||
                    !this._companiesSubject.value.length) &&
                !this._companiesSubscription &&
                this.isLoggedIn)
        ) {
            this._companiesSubscription = this.http
                .get(`${this.apiUrl}/companies/`)
                .subscribe(
                    (res: any[]) => {
                        let items = res.map((value) => {
                            try {
                                return new Company().setFromApiModel(value);
                            } catch (e) {
                                return Company.getEmptyModel(0);
                            }
                        });
                        items.forEach((c) => this.storage.addCompany(c.id));
                        this.updateCompanies(items);
                    },
                    (error) => {
                        this.logger.error(error);
                        this.storage.companiesId = [];
                        this.updateCompanies(null);
                    }
                );
        }
    }

    /**
     * Both updateCurrentCompany and getCurrentCompany fetch the current company, and on login they
     * do it in the same tick: CompanyGroupService.getTreeSubscription calls setCurrentCompany and
     * then pushes the tree, which is what sends the login page and PermissionsService here. Sharing
     * the in-flight request keeps that to one GET instead of one per caller.
     */
    private fetchCompany(id: number): Observable<Company> {
        const inFlight = this._companyRequests.get(id);
        if (inFlight) {
            return inFlight;
        }
        const request = this.http.get(`${this.apiUrl}/companies/${id}/`).pipe(
            map(
                (res: any) => {
                    let company = new Company().setFromApiModel(res);
                    this.storage.setStorageItem('currency', company.currency);
                    this.storage.setStorageItem('currentUserRole', company.currentUserRole);
                    let userRole = (<Company>(company)).getRoleName();
                    this.storage.todayOnly = userRole ? todayOnlyRoles.indexOf(String(userRole).toLowerCase()) >= 0 : false;
                    this.storage.advancedSubscription = company.isAdvancedSubscription();
                    return company;
                }
            ),
            finalize(() => this._companyRequests.delete(id)),
            // refCount so that when every subscriber leaves before the response the request is
            // still cancelled, as it was before this was shared. Otherwise the map above would go
            // on to write currency/role/todayOnly for a company the app has navigated away from.
            shareReplay({bufferSize: 1, refCount: true})
        );
        this._companyRequests.set(id, request);
        return request;
    }

    getCurrentCompany(refresh?: boolean): Observable<Company> {
        if (this.storage.companyId && (refresh || !this._initialized)) {
            this._initialized = true;
            let changed =
                !this._currentCompanySubject?.value ||
                this._currentCompanySubject.value.id !== this.storage.companyId;
            if (
                this._currentCompanySubject &&
                this._currentCompanySubject.value !== null &&
                this._currentCompanySubject.value.id !== this.storage.companyId
            ) {
                this._currentCompanySubject = new BehaviorSubject<Company>(
                    null
                );
            }
            return this.fetchCompany(this.storage.companyId).pipe(
                map((value) => {
                    this._currentCompanySubject.next(value);
                    if (changed) {
                        this._currentCompanyChanged.next(value);
                    }
                    return value;
                })
            );
        } else {
            // this.fetchCompanies(refresh);
            return this._currentCompanySubject.asObservable();
        }
    }

    getAllCompanies(withoutUpdate?: boolean): Observable<Company[]> {
        return this.http.get(`${this.apiUrl}/companies/`).pipe(
            map((data: any[]) => {
                let res = [];
                data.forEach((item) => {
                    res.push(new Company().setFromApiModel(item));
                });
                if (res.length) {
                    if (!withoutUpdate) {
                        this.updateCompanies(res);
                    }
                    return res;
                } else {
                    return [];
                }
            })
        );
    }

    integrateExternalService(model: any, log?): Observable<any> {
        let companyId = this.storage.companyId;
        return this.http
            .post(`${this.apiUrl}/companies/${companyId}/services/`, model)
            .pipe(
                map((data: any) => {
                    return new IntegrationsModel().setFromApiModel(data);
                }),
                tap({
                    next: val => {
                        if (log && log.info && log.target) {
                            this.activityTrackerService.log(log.info, log.target);
                        }
                    },
                    error: error => {
                      console.log('on error', error.message);
                    }
                })
            );
    }

    getServiceAccounts(serviceId: number): Observable<any> {
        let companyId = this.storage.companyId;
        return this.http.get(`${this.apiUrl}/companies/${companyId}/services/${serviceId}/info/`).pipe(
            map((data: any) => {
                return data;
            })
        );
    }

    updateServiceAccounts(serviceId: number, model, log?): Observable<any> {
        let companyId = this.storage.companyId;
        return this.http.post(`${this.apiUrl}/companies/${companyId}/services/${serviceId}/info/`,
            model
        )
            .pipe(
                map((data: any) => {
                    return data;
                }),
                tap({
                    next: val => {
                        if (log && log.info && log.target) {
                            this.activityTrackerService.log(log.info, log.target);
                        }
                    },
                    error: error => {
                      console.log('on error', error.message);
                    }
                })
            );
    }

    deleteIntegrateExternalService(id: number, clearHistory?: boolean, log?) {
        let companyId = this.storage.companyId;
        let params: HttpParams = new HttpParams();
        if (clearHistory) {
            params = params.append('full', true);
        }
        return this.http.delete(
            `${this.apiUrl}/companies/${companyId}/services/${id}/`,
            {params: params}
        )
        .pipe(
            map((data) => {
                return data;
            }),
            tap({
                next: val => {
                    if (log && log.info && log.target) {
                        this.activityTrackerService.log(log.info, log.target);
                    }
                },
                error: error => {
                  console.log('on error', error.message);
                }
            })
        );
    }

    getIntegrationsAll(): Observable<any> {
        return this.http.get(`${this.apiUrl}/integrations/`).pipe(
            map((data: any) => {
                return get(data, 'results').map((i) =>
                    new IntegrationsModel().setFromApiModelAll(i)
                );
            })
        );
    }

    syncExternalService(serviceId: string, body: any, log?): Observable<any> {
        let companyId = this.storage.companyId;
        return this.http
            .post(
                `${this.apiUrl}/companies/${companyId}/services/${serviceId}/data/`,
                body
            )
            .pipe(
                map((data: any) => {
                    return data;
                }),
                tap({
                    next: val => {
                        if (log && log.info && log.target) {
                            this.activityTrackerService.log(log.info, log.target);
                        }
                    },
                    error: error => {
                      console.log('on error', error.message);
                    }
                })
            );
    }

    saveCompany(company: Company, log?): Observable<Company> {
        if (company.id) {
            //update info
            return this.http
                .patch(
                    `${this.apiUrl}/companies/${company.id}/`,
                    company.getApiModel()
                )
                .pipe(
                    map((data) => {
                        let item = new Company().setFromApiModel(data);
                        this.storage.addCompany(item.id);
                        this.updateCompaniesWithUpdatedItem(item);
                        return item;
                    }),
                    tap({
                        next: val => {
                            if (log && log.info && log.target) {
                                this.activityTrackerService.log(log.info, log.target);
                            }
                        },
                        error: error => {
                          console.log('on error', error.message);
                        }
                    })
                );
        } else {
            //create new
            return this.http
                .post(`${this.apiUrl}/companies/`, company.getApiModel())
                .pipe(
                    map((data) => {
                        data['current_user_groups'] = ['Owner'];
                        let item = new Company().setFromApiModel(data);
                        this.updateCompaniesWithUpdatedItem(item);
                        this.intercom.trackEvent('company-created');
                        return item;
                    }),
                    tap({
                        next: val => {
                            if (log && log.info && log.target) {
                                this.activityTrackerService.log(log.info, log.target);
                            }
                        },
                        error: error => {
                          console.log('on error', error.message);
                        }
                    })
                );
        }
    }

    updateCompanyParent(item: any, log?): Observable<Company> {
        if (item.id) {
            const formData = new FormData();
            formData.append('id', String(item.id));
            if (item.companyGroup) {
                formData.append('corporate_group', String(item.companyGroup));
            } else {
                formData.append('corporate_group', null);
            }
            if (item.order) {
                formData.append('order', String(item.order));
            }
            return this.http
                .patch(`${this.apiUrl}/companies/${item.id}/`, formData)
                .pipe(
                    map((data) => {
                        let item = new Company().setFromApiModel(data);
                        // this.storage.addCompany(item.id);
                        this.updateCompaniesWithUpdatedItem(item);
                        return item;
                    }),
                    tap({
                        next: val => {
                            if (log && log.info && log.target) {
                                this.activityTrackerService.log(log.info, log.target);
                            }
                        },
                        error: error => {
                          console.log('on error', error.message);
                        }
                    })
                );
        }
    }

    updateCompanyOrder(item: any, log?): Observable<Company> {
        if (item.id) {
            const formData = new FormData();
            formData.append('id', String(item.id));
            formData.append('order', String(item.order));
            return this.http
                .patch(`${this.apiUrl}/companies/${item.id}/`, formData)
                .pipe(
                    map((data) => {
                        let item = new Company().setFromApiModel(data);
                        //  this.storage.addCompany(item.id);
                        this.updateCompaniesWithUpdatedItem(item);
                        return item;
                    }),
                    tap({
                        next: val => {
                            if (log && log.info && log.target) {
                                this.activityTrackerService.log(log.info, log.target);
                            }
                        },
                        error: error => {
                          console.log('on error', error.message);
                        }
                    })
                );
        }
    }

    getIntercomSettings(): Observable<IntercomSettings> {
        const companyId = this.storage.companyId;
        return this.http
            .get(`${this.apiUrl}/companies/${companyId}/info/`)
            .pipe(
                map((res: any) => {
                    return new IntercomSettings().setFromApiModel(res, true);
                })
            );
    }

    changeCompany(companyId: number) {
        this.storage.companyId = companyId;
        this.storage.selectedLevel = {id: companyId, type: LevelType.Company};
        let company = this.companiesList.find((item) => item.id === companyId);
        this.updateCurrentCompany(company || null);
    }

    /**
     * Opens the switch window. While open, the route guard aborts the outgoing page's stale
     * reload:false self-navigation so the sanctioned reload:true re-entry survives. A safety
     * timeout guarantees the window always closes even if the re-navigation never settles.
     */
    beginCompanySwitch(): void {
        this.switchInProgress = true;
        clearTimeout(this._switchTimer);
        this._switchTimer = setTimeout(() => this.endCompanySwitch(), 8000);
    }

    endCompanySwitch(): void {
        this.switchInProgress = false;
        clearTimeout(this._switchTimer);
        this._switchTimer = null;
    }

    deleteCompany(companyId: number, log?) {
        return this.http.delete(`${this.apiUrl}/companies/${companyId}/`).pipe(
            map((res: any) => {
                let companies = this._companiesSubject.value;
                let existingItemIndex = companies
                    ? companies.findIndex((i) => i.id === companyId)
                    : null;
                if (existingItemIndex >= 0) {
                    companies.splice(existingItemIndex, 1);
                    this.updateCompanies(companies);
                }
            }),
            tap({
                next: val => {
                    if (log && log.info && log.target) {
                        this.activityTrackerService.log(log.info, log.target);
                    }
                },
                error: error => {
                  console.log('on error', error.message);
                }
            })
        );
    }

    updateCompany(companyId: number, data, log?) {
        return this.http
            .patch(`${this.apiUrl}/companies/${companyId}/`, data)
            .pipe(
                map((res: any) => {
                    let item = new Company().setFromApiModel(res);
                    this.updateCompaniesWithUpdatedItem(item);
                    if (this.storage.companyId === companyId) {
                        this._currentCompanySubject.next(item);
                    }
                    return item;
                }),
                tap({
                    next: val => {
                        if (log && log.info && log.target) {
                            this.activityTrackerService.log(log.info, log.target);
                        }
                    },
                    error: error => {
                      console.log('on error', error.message);
                    }
                })
            );
    }

    addSlackToken(slack, log?) {
        let companyId = this.storage.companyId;
        return this.http
            .post(
                `${this.apiUrl}/companies/${companyId}/slack/auth/`,
                new SlackAuth(slack).getApiModel()
            )
            .pipe(
                map((data) => {
                    return new SlackAuth().setFromApiModel(data);
                }),
                tap({
                    next: val => {
                        if (log && log.info && log.target) {
                            this.activityTrackerService.log(log.info, log.target);
                        }
                    },
                    error: error => {
                      console.log('on error', error.message);
                    }
                })
            );
    }

    getSlackTokens() {
        let companyId = this.storage.companyId;
        return this.http
            .get(`${this.apiUrl}/companies/${companyId}/slack/auth/`)
            .pipe(
                map((data: any) => {
                    return data.map((value) => {
                        return new SlackAuth().setFromApiModel(value);
                    });
                })
            );
    }

    updateSlackToken(slack, log?) {
        let companyId = this.storage.companyId;
        return this.http
            .put(
                `${this.apiUrl}/companies/${companyId}/slack/auth/${slack.id}/`,
                new SlackAuth(slack).getApiModel()
            )
            .pipe(
                map((data) => {
                    return data;
                }),
                tap({
                    next: val => {
                        if (log && log.info && log.target) {
                            this.activityTrackerService.log(log.info, log.target);
                        }
                    },
                    error: error => {
                      console.log('on error', error.message);
                    }
                })
            );
    }

    deleteSlackToken(slack, log?) {
        let companyId = this.storage.companyId;
        return this.http.delete(
            `${this.apiUrl}/companies/${companyId}/slack/auth/${slack.id}/`
        )
        .pipe(
            map((data) => {
                return data;
            }),
            tap({
                next: val => {
                    if (log && log.info && log.target) {
                        this.activityTrackerService.log(log.info, log.target);
                    }
                },
                error: error => {
                  console.log('on error', error.message);
                }
            })
        );
    }

    getMemberProfile(companyId?: number): Observable<any> {
        if (!companyId) {
            companyId = this.storage.companyId;
        }
        return this.http
            .get(`${this.apiUrl}/companies/${companyId}/member-profile/`)
            .pipe(
                map((data: any) => {
                    return data;
                })
            );
    }

    updateMemberProfile(value: boolean, companyId?: number, log?): Observable<any> {
        if (!companyId) {
            companyId = this.storage.companyId;
        }
        return this.http
            .patch(`${this.apiUrl}/companies/${companyId}/member-profile/`, {
                weekly_report: value,
            })
            .pipe(
                map((data: any) => {
                    return data;
                }),
                tap({
                    next: val => {
                        if (log && log.info && log.target) {
                            this.activityTrackerService.log(log.info, log.target);
                        }
                    },
                    error: error => {
                      console.log('on error', error.message);
                    }
                })
            );
    }

    updateMember(item: any, companyId?: any, log?): Observable<any> {
        if (!companyId) {
            companyId = this.storage.companyId;
        }
        return this.http
            .patch(
                `${this.apiUrl}/companies/${companyId}/member-profile/`,
                item
            )
            .pipe(
                map((data: any) => {
                    return data;
                }),
                tap({
                    next: val => {
                        if (log && log.info && log.target) {
                            this.activityTrackerService.log(log.info, log.target);
                        }
                    },
                    error: error => {
                      console.log('on error', error.message);
                    }
                })
            );
    }

    getSyncErrors(): Observable<any> {
        let companyId = this.storage.companyId;
        return this.http
            .get(
                `${this.apiUrl}/companies/${companyId}/activities/?message=sync_failed&unread=true`
            )
            .pipe(
                map((data: any) => {
                    return data.results;
                })
            );
    }

    markSyncErrors(data: any): Observable<any> {
        let companyId = this.storage.companyId;
        return this.http.put(
            `${this.apiUrl}/companies/${companyId}/activities/`,
            data
        );
    }

    declineInvitationCompany(memberId: number, targetCompanyId: number, log?): Observable<any> {
        return this.http.delete(
            `${this.apiUrl}/companies/${targetCompanyId}/members/${memberId}`
        ).pipe(
            map((res: any) => {
                let companies = this._companiesSubject.value;
                let existingItemIndex = companies
                    ? companies.findIndex((i) => i.id === targetCompanyId)
                    : null;
                if (existingItemIndex >= 0) {
                    companies.splice(existingItemIndex, 1);
                    this.updateCompanies(companies);
                }
            }),
            tap({
                next: val => {
                    if (log && log.info && log.target) {
                        this.activityTrackerService.log(log.info, log.target);
                    }
                },
                error: error => {
                  console.log('on error', error.message);
                }
            })
        );
    }

    declineInvitationGroup(memberId: number, groupId: number, log?): Observable<any> {
        return this.http.delete(
            `${this.apiUrl}/corporate-groups/${groupId}/members/${memberId}/`
        ).pipe(
            map((res: any) => {
                let companies = this._companiesSubject.value;
                for (let i = companies.length; i--; i >= 0) {
                    if (companies[i].parent && companies[i].parent === groupId) {
                        companies.splice(i, 1);
                    }
                }
                this.updateCompanies(companies);
            }),
            tap({
                next: val => {
                    if (log && log.info && log.target) {
                        this.activityTrackerService.log(log.info, log.target);
                    }
                },
                error: error => {
                    console.log('on error', error.message);
                }
            })
        );
    }
}
